'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { getInvoices, getTimeLogs, formatCurrency, getSenderProfile, saveSenderProfile, SenderProfile } from '@/lib/storage';

// ─── Dashboard Preferences ───────────────────────────────────────────────────
const DASH_PREFS_KEY = 'hub_dashboard_prefs';

interface DashPrefs {
  hubOrder: string[];           // hub hrefs in display order
  hiddenHubs: string[];         // hrefs of hidden hub cards
  hiddenStats: string[];        // stat labels hidden
  hiddenQuickLinks: string[];   // quick-link hrefs hidden
  layout: 'comfortable' | 'compact' | 'spacious';
  showProfile: boolean;
  showStats: boolean;
  showQuickLinks: boolean;
}

const DEFAULT_HUB_ORDER = ['/hub/live', '/map', '/hub/bike-nav', '/hub/ebike-race', '/dashboard', '/clients'];

const DEFAULT_PREFS: DashPrefs = {
  hubOrder: DEFAULT_HUB_ORDER,
  hiddenHubs: [],
  hiddenStats: [],
  hiddenQuickLinks: [],
  layout: 'comfortable',
  showProfile: true,
  showStats: true,
  showQuickLinks: true,
};

function loadPrefs(): DashPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(DASH_PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const saved = JSON.parse(raw) as Partial<DashPrefs>;
    return { ...DEFAULT_PREFS, ...saved };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: DashPrefs) {
  try { localStorage.setItem(DASH_PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

// ─── Hub Definitions ──────────────────────────────────────────────────────────
const ALL_HUBS = [
  {
    href: '/hub/live',
    label: 'Live Dashboard',
    sub: 'GPS · Invoices · Jobs · Music',
    color: '#F87171',
    bg: 'rgba(248,113,113,0.08)',
    border: 'rgba(248,113,113,0.15)',
    span: 'col-span-2',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="14" cy="14" r="4" stroke="#F87171" strokeWidth="1.8"/>
        <circle cx="14" cy="14" r="1.8" fill="#F87171"/>
        <path d="M14 4v3M14 21v3M4 14h3M21 14h3" stroke="#F87171" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="14" cy="14" r="9" stroke="#F87171" strokeWidth="1" strokeDasharray="2 3" opacity="0.4"/>
      </svg>
    ),
  },
  {
    href: '/map',
    label: 'Smart Map',
    sub: 'Roads · Search · Navigation',
    color: '#3B9EFF',
    bg: 'rgba(59,158,255,0.08)',
    border: 'rgba(59,158,255,0.15)',
    span: '',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path d="M14 3C9.58 3 6 6.58 6 11c0 6.56 8 14 8 14s8-7.44 8-14c0-4.42-3.58-8-8-8z" stroke="#3B9EFF" strokeWidth="1.8"/>
        <circle cx="14" cy="11" r="3" stroke="#3B9EFF" strokeWidth="1.6"/>
        <path d="M3 22l5-2 8 3 9-3" stroke="#3B9EFF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
      </svg>
    ),
  },
  {
    href: '/hub/bike-nav',
    label: 'Bike Navigation',
    sub: 'Speed · Turn-by-turn · km',
    color: '#00E676',
    bg: 'rgba(0,230,118,0.08)',
    border: 'rgba(0,230,118,0.15)',
    span: '',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="7" cy="20" r="4.5" stroke="#00E676" strokeWidth="1.8"/>
        <circle cx="21" cy="20" r="4.5" stroke="#00E676" strokeWidth="1.8"/>
        <path d="M7 20L11 9h5l3 5" stroke="#00E676" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M11 9h5" stroke="#00E676" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="16" cy="9" r="1.5" fill="#00E676"/>
        <path d="M16 9l5 11" stroke="#00E676" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    href: '/hub/ebike-race',
    label: 'eBike Race',
    sub: 'Speed · Power · Music',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.08)',
    border: 'rgba(245,158,11,0.15)',
    span: '',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path d="M4 14h5l3-7 4 14 3-8 3 5h2" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="14" cy="14" r="10" stroke="#F59E0B" strokeWidth="1.2" strokeDasharray="3 3" opacity="0.3"/>
      </svg>
    ),
  },
  {
    href: '/dashboard',
    label: 'Finance',
    sub: 'Invoices · Revenue · Stats',
    color: '#60A5FA',
    bg: 'rgba(96,165,250,0.08)',
    border: 'rgba(96,165,250,0.15)',
    span: '',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect x="3" y="3" width="10" height="10" rx="2.5" fill="#60A5FA" opacity="0.8"/>
        <rect x="15" y="3" width="10" height="10" rx="2.5" fill="#60A5FA" opacity="0.4"/>
        <rect x="3" y="15" width="10" height="10" rx="2.5" fill="#60A5FA" opacity="0.4"/>
        <rect x="15" y="15" width="10" height="10" rx="2.5" fill="#60A5FA" opacity="0.8"/>
      </svg>
    ),
  },
  {
    href: '/clients',
    label: 'Clients',
    sub: 'Directory · ABN · Terms',
    color: '#34D399',
    bg: 'rgba(52,211,153,0.08)',
    border: 'rgba(52,211,153,0.15)',
    span: '',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="11" cy="10" r="4.5" stroke="#34D399" strokeWidth="1.8"/>
        <path d="M3 24c0-4.42 3.58-7 8-7s8 2.58 8 7" stroke="#34D399" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="22" cy="11" r="3" stroke="#34D399" strokeWidth="1.6" opacity="0.6"/>
        <path d="M25 23c0-2.76-1.34-4.5-3-4.5" stroke="#34D399" strokeWidth="1.6" strokeLinecap="round" opacity="0.6"/>
      </svg>
    ),
  },
];

const ALL_QUICK_LINKS = [
  { href: '/invoices', label: 'All Invoices', icon: '📄', color: '#60A5FA' },
  { href: '/time-logs', label: 'Time Logs', icon: '⏱', color: '#F59E0B' },
  { href: '/analytics', label: 'Analytics', icon: '📊', color: '#A78BFA' },
  { href: '/alerts', label: 'Alerts', icon: '🔔', color: '#F87171' },
];

const ALL_STAT_LABELS = ['Revenue', 'Overdue', 'Active Jobs', 'Hours Logged'];

interface QuickStat { label: string; value: string; sub: string; color: string; }

// ─── Toggle Switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="relative flex-shrink-0 rounded-full transition-colors duration-200"
      style={{
        width: 40, height: 22,
        background: checked ? 'var(--green)' : 'rgba(168,176,186,0.2)',
        border: '1px solid ' + (checked ? 'rgba(0,230,118,0.4)' : 'rgba(168,176,186,0.15)'),
      }}
      aria-checked={checked}
      role="switch"
    >
      <span
        className="absolute top-0.5 rounded-full transition-transform duration-200"
        style={{
          width: 16, height: 16,
          background: checked ? '#0F1318' : 'rgba(168,176,186,0.6)',
          transform: checked ? 'translateX(20px)' : 'translateX(2px)',
        }}
      />
    </button>
  );
}

// ─── Drag-to-reorder row ───────────────────────────────────────────────────────
function DraggableHubRow({
  hub, hidden, onToggle, onDragStart, onDragOver, onDrop, isDragging,
}: {
  hub: typeof ALL_HUBS[0];
  hidden: boolean;
  onToggle: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  isDragging: boolean;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={e => { e.preventDefault(); onDragOver(e); }}
      onDrop={onDrop}
      className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all"
      style={{
        background: isDragging ? 'rgba(59,158,255,0.08)' : 'rgba(168,176,186,0.04)',
        border: '1px solid ' + (isDragging ? 'rgba(59,158,255,0.2)' : 'rgba(168,176,186,0.08)'),
        opacity: hidden ? 0.45 : 1,
        cursor: 'grab',
      }}
    >
      {/* drag handle */}
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: 'var(--chrome-dim)', flexShrink: 0 }}>
        <circle cx="4" cy="3.5" r="1.2" fill="currentColor"/>
        <circle cx="10" cy="3.5" r="1.2" fill="currentColor"/>
        <circle cx="4" cy="7" r="1.2" fill="currentColor"/>
        <circle cx="10" cy="7" r="1.2" fill="currentColor"/>
        <circle cx="4" cy="10.5" r="1.2" fill="currentColor"/>
        <circle cx="10" cy="10.5" r="1.2" fill="currentColor"/>
      </svg>
      {/* icon */}
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: hub.bg }}>
        <div style={{ transform: 'scale(0.7)', transformOrigin: 'center' }}>{hub.icon}</div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: 'var(--paper)' }}>{hub.label}</p>
        <p className="text-xs truncate" style={{ color: 'var(--chrome-dim)' }}>{hub.sub}</p>
      </div>
      <Toggle checked={!hidden} onChange={onToggle} />
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function HubPage() {
  const [stats, setStats] = useState<QuickStat[]>([]);
  const [mounted, setMounted] = useState(false);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profile, setProfile] = useState<SenderProfile>({ name: '', email: '', company: '', address: '', abn: '' });

  // Dashboard prefs
  const [prefs, setPrefs] = useState<DashPrefs>(DEFAULT_PREFS);
  const [panelOpen, setPanelOpen] = useState(false);

  // Drag state
  const dragIdx = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  useEffect(() => {
    const invoices = getInvoices();
    const logs = getTimeLogs();
    const revenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0);
    const overdue = invoices.filter(i => i.status === 'overdue').length;
    const activeLogs = logs.filter(l => l.status === 'active').length;
    const totalHours = logs.reduce((s, l) => s + l.durationMinutes, 0) / 60;

    setStats([
      { label: 'Revenue', value: formatCurrency(revenue), sub: `${invoices.filter(i => i.status === 'paid').length} paid`, color: '#00E676' },
      { label: 'Overdue', value: String(overdue), sub: 'invoices', color: overdue > 0 ? '#F87171' : 'var(--chrome)' },
      { label: 'Active Jobs', value: String(activeLogs), sub: 'running now', color: activeLogs > 0 ? '#F59E0B' : 'var(--chrome)' },
      { label: 'Hours Logged', value: `${totalHours.toFixed(0)}h`, sub: 'total tracked', color: '#3B9EFF' },
    ]);

    const saved = getSenderProfile();
    setProfile({ name: saved.name || '', email: saved.email || '', company: saved.company || '', address: saved.address || '', abn: saved.abn || '' });

    setPrefs(loadPrefs());
    setMounted(true);
  }, []);

  // Persist prefs whenever they change (after mount)
  useEffect(() => {
    if (mounted) savePrefs(prefs);
  }, [prefs, mounted]);

  function handleProfileSave() {
    saveSenderProfile(profile);
    setProfileEditing(false);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2500);
  }

  // ── Pref helpers ──
  const updatePrefs = useCallback((patch: Partial<DashPrefs>) => {
    setPrefs(p => ({ ...p, ...patch }));
  }, []);

  function toggleHub(href: string) {
    setPrefs(p => ({
      ...p,
      hiddenHubs: p.hiddenHubs.includes(href)
        ? p.hiddenHubs.filter(h => h !== href)
        : [...p.hiddenHubs, href],
    }));
  }

  function toggleStat(label: string) {
    setPrefs(p => ({
      ...p,
      hiddenStats: p.hiddenStats.includes(label)
        ? p.hiddenStats.filter(l => l !== label)
        : [...p.hiddenStats, label],
    }));
  }

  function toggleQuickLink(href: string) {
    setPrefs(p => ({
      ...p,
      hiddenQuickLinks: p.hiddenQuickLinks.includes(href)
        ? p.hiddenQuickLinks.filter(h => h !== href)
        : [...p.hiddenQuickLinks, href],
    }));
  }

  function resetPrefs() {
    setPrefs(DEFAULT_PREFS);
  }

  // ── Drag reorder ──
  function handleDragStart(idx: number) {
    dragIdx.current = idx;
  }

  function handleDragOver(idx: number) {
    setDragOver(idx);
  }

  function handleDrop(dropIdx: number) {
    const from = dragIdx.current;
    if (from === null || from === dropIdx) { dragIdx.current = null; setDragOver(null); return; }
    const newOrder = [...prefs.hubOrder];
    const [moved] = newOrder.splice(from, 1);
    newOrder.splice(dropIdx, 0, moved);
    updatePrefs({ hubOrder: newOrder });
    dragIdx.current = null;
    setDragOver(null);
  }

  if (!mounted) return null;

  // Build ordered hub list
  const orderedHubs = [...prefs.hubOrder]
    .map(href => ALL_HUBS.find(h => h.href === href))
    .filter(Boolean) as typeof ALL_HUBS;
  // Add any hubs not yet in order (future-proofing)
  ALL_HUBS.forEach(h => { if (!prefs.hubOrder.includes(h.href)) orderedHubs.push(h); });

  const visibleHubs = orderedHubs.filter(h => !prefs.hiddenHubs.includes(h.href));
  const visibleStats = stats.filter(s => !prefs.hiddenStats.includes(s.label));
  const visibleQuickLinks = ALL_QUICK_LINKS.filter(l => !prefs.hiddenQuickLinks.includes(l.href));

  const gapClass = prefs.layout === 'compact' ? 'gap-2' : prefs.layout === 'spacious' ? 'gap-6' : 'gap-4';
  const padClass = prefs.layout === 'compact' ? 'p-3' : prefs.layout === 'spacious' ? 'p-7' : 'p-5';

  const inputStyle = { background: 'rgba(168,176,186,0.06)', border: '1px solid rgba(168,176,186,0.12)', color: 'var(--paper)' };

  return (
    <div className="min-h-screen" style={{ background: 'var(--gunmetal)' }}>
      <AppNav />

      {/* ── Customise Panel Overlay ── */}
      {panelOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={() => setPanelOpen(false)}>
          <div
            className="relative h-full overflow-y-auto flex flex-col"
            style={{ width: '100%', maxWidth: 400, background: 'var(--gunmetal)', borderLeft: '1px solid rgba(168,176,186,0.1)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 sticky top-0 z-10" style={{ background: 'var(--gunmetal)', borderBottom: '1px solid rgba(168,176,186,0.08)' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59,158,255,0.1)' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 4h12M2 8h8M2 12h5" stroke="#3B9EFF" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="13" cy="8" r="2" stroke="#3B9EFF" strokeWidth="1.3"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--paper)' }}>Customise Dashboard</p>
                  <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>Personalise your hub layout</p>
                </div>
              </div>
              <button onClick={() => setPanelOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors" style={{ background: 'rgba(168,176,186,0.08)', color: 'var(--chrome-dim)' }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
            </div>

            <div className="flex-1 px-4 py-4 space-y-5">

              {/* Layout density */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--chrome-dim)' }}>Layout Density</p>
                <div className="grid grid-cols-3 gap-2">
                  {(['compact', 'comfortable', 'spacious'] as const).map(opt => (
                    <button
                      key={opt}
                      onClick={() => updatePrefs({ layout: opt })}
                      className="rounded-xl py-2.5 text-xs font-medium capitalize transition-all"
                      style={{
                        background: prefs.layout === opt ? 'rgba(59,158,255,0.15)' : 'rgba(168,176,186,0.06)',
                        border: '1px solid ' + (prefs.layout === opt ? 'rgba(59,158,255,0.35)' : 'rgba(168,176,186,0.1)'),
                        color: prefs.layout === opt ? '#3B9EFF' : 'var(--chrome)',
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Section visibility */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--chrome-dim)' }}>Sections</p>
                <div className="space-y-2">
                  {[
                    { key: 'showStats' as const, label: 'Quick Stats', sub: 'Revenue, overdue, jobs, hours' },
                    { key: 'showProfile' as const, label: 'Sender Profile', sub: 'Your invoice details' },
                    { key: 'showQuickLinks' as const, label: 'Quick Links', sub: 'Invoices, logs, analytics, alerts' },
                  ].map(s => (
                    <div key={s.key} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: 'rgba(168,176,186,0.04)', border: '1px solid rgba(168,176,186,0.08)' }}>
                      <div className="flex-1">
                        <p className="text-sm font-medium" style={{ color: 'var(--paper)' }}>{s.label}</p>
                        <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>{s.sub}</p>
                      </div>
                      <Toggle checked={prefs[s.key]} onChange={v => updatePrefs({ [s.key]: v })} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Hub cards — drag to reorder */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--chrome-dim)' }}>Hub Cards</p>
                <p className="text-xs mb-3" style={{ color: 'var(--chrome-dim)', opacity: 0.7 }}>Drag to reorder · toggle to show/hide</p>
                <div className="space-y-2">
                  {orderedHubs.map((hub, idx) => (
                    <DraggableHubRow
                      key={hub.href}
                      hub={hub}
                      hidden={prefs.hiddenHubs.includes(hub.href)}
                      onToggle={() => toggleHub(hub.href)}
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={() => handleDragOver(idx)}
                      onDrop={() => handleDrop(idx)}
                      isDragging={dragOver === idx}
                    />
                  ))}
                </div>
              </div>

              {/* Stat toggles */}
              {prefs.showStats && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--chrome-dim)' }}>Stat Cards</p>
                  <div className="space-y-2">
                    {ALL_STAT_LABELS.map(label => (
                      <div key={label} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: 'rgba(168,176,186,0.04)', border: '1px solid rgba(168,176,186,0.08)' }}>
                        <p className="flex-1 text-sm font-medium" style={{ color: 'var(--paper)' }}>{label}</p>
                        <Toggle checked={!prefs.hiddenStats.includes(label)} onChange={() => toggleStat(label)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick link toggles */}
              {prefs.showQuickLinks && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--chrome-dim)' }}>Quick Links</p>
                  <div className="space-y-2">
                    {ALL_QUICK_LINKS.map(l => (
                      <div key={l.href} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: 'rgba(168,176,186,0.04)', border: '1px solid rgba(168,176,186,0.08)' }}>
                        <span className="text-base">{l.icon}</span>
                        <p className="flex-1 text-sm font-medium" style={{ color: 'var(--paper)' }}>{l.label}</p>
                        <Toggle checked={!prefs.hiddenQuickLinks.includes(l.href)} onChange={() => toggleQuickLink(l.href)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Panel footer */}
            <div className="px-4 py-4 sticky bottom-0" style={{ background: 'var(--gunmetal)', borderTop: '1px solid rgba(168,176,186,0.08)' }}>
              <button
                onClick={resetPrefs}
                className="w-full rounded-xl py-3 text-sm font-medium transition-all"
                style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.15)', color: '#F87171' }}
              >
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 pb-16">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="hb-body text-2xl font-semibold mb-1" style={{ color: 'var(--paper)', letterSpacing: '-0.02em' }}>Dashboard Hub</h1>
              <p className="text-sm hb-landscape-hide" style={{ color: 'var(--chrome-dim)' }}>Select a workspace to get started</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Customise button */}
              <button
                onClick={() => setPanelOpen(true)}
                className="hb-touch hb-tap-feedback flex items-center gap-2 px-4 rounded-xl text-sm font-medium transition-all"
                style={{ background: 'rgba(59,158,255,0.1)', border: '1px solid rgba(59,158,255,0.2)', color: '#3B9EFF', minHeight: 48 }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 3h12M1 7h8M1 11h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  <circle cx="12" cy="7" r="2" stroke="currentColor" strokeWidth="1.3"/>
                </svg>
                <span className="hidden sm:inline">Customise</span>
              </button>
              <Link href="/invoices/new" className="hb-touch hb-tap-feedback flex items-center gap-2 px-4 rounded-xl text-sm font-semibold" style={{ background: 'var(--green)', color: '#0F1318', minHeight: 48 }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                New Invoice
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {prefs.showStats && visibleStats.length > 0 && (
          <div className={`grid grid-cols-2 lg:grid-cols-4 ${gapClass} mb-6 hb-stat-grid`}>
            {visibleStats.map(s => (
              <div key={s.label} className="card-elevated p-4">
                <p className="hb-label text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--chrome-dim)' }}>{s.label}</p>
                <p className="hb-stat font-mono font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--chrome-dim)' }}>{s.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* Sender Profile Card */}
        {prefs.showProfile && (
          <div className={`card-elevated rounded-2xl ${padClass} sm:p-6 mb-6`}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(59,158,255,0.1)' }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="7" r="3.5" stroke="#3B9EFF" strokeWidth="1.6"/>
                    <path d="M3 17c0-3.31 3.13-6 7-6s7 2.69 7 6" stroke="#3B9EFF" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-semibold" style={{ color: 'var(--paper)' }}>Sender Profile</h2>
                  <p className="text-xs hb-landscape-hide" style={{ color: 'var(--chrome-dim)' }}>Auto-fills your details on new invoices</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {profileSaved && (
                  <span className="hb-badge text-xs font-medium px-2.5 rounded-lg" style={{ background: 'rgba(0,230,118,0.12)', color: 'var(--green)' }}>
                    ✓ Saved
                  </span>
                )}
                {!profileEditing ? (
                  <button
                    onClick={() => setProfileEditing(true)}
                    className="hb-touch hb-tap-feedback flex items-center gap-1.5 px-3 rounded-lg text-xs font-medium transition-all"
                    style={{ background: 'rgba(59,158,255,0.1)', color: '#3B9EFF', border: '1px solid rgba(59,158,255,0.2)', minHeight: 48 }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8.5 1.5l2 2L4 10H2v-2L8.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Edit Profile
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setProfileEditing(false); const saved = getSenderProfile(); setProfile({ name: saved.name || '', email: saved.email || '', company: saved.company || '', address: saved.address || '', abn: saved.abn || '' }); }}
                      className="hb-touch px-3 rounded-lg text-xs font-medium"
                      style={{ background: 'rgba(168,176,186,0.08)', color: 'var(--chrome-dim)', border: '1px solid rgba(168,176,186,0.12)', minHeight: 48 }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProfileSave}
                      className="hb-touch hb-tap-feedback flex items-center gap-1.5 px-3 rounded-lg text-xs font-semibold"
                      style={{ background: 'var(--green)', color: '#0F1318', minHeight: 48 }}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Save Profile
                    </button>
                  </div>
                )}
              </div>
            </div>

            {profileEditing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Your Name', key: 'name' as keyof SenderProfile, placeholder: 'John Doe' },
                  { label: 'Your Email', key: 'email' as keyof SenderProfile, placeholder: 'john@yourcompany.com' },
                  { label: 'Company', key: 'company' as keyof SenderProfile, placeholder: 'Your Company Ltd' },
                  { label: 'Address', key: 'address' as keyof SenderProfile, placeholder: '456 Business Ave, City' },
                  { label: 'ABN', key: 'abn' as keyof SenderProfile, placeholder: '12 345 678 901' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs mb-1.5" style={{ color: 'var(--chrome-dim)' }}>{f.label}</label>
                    <input
                      value={profile[f.key] || ''}
                      onChange={e => setProfile(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      className="w-full rounded-lg px-3 text-sm outline-none"
                      style={{ ...inputStyle, minHeight: 48 }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { label: 'Name', value: profile.name },
                  { label: 'Email', value: profile.email },
                  { label: 'Company', value: profile.company },
                  { label: 'Address', value: profile.address },
                  { label: 'ABN', value: profile.abn },
                ].map(f => (
                  <div key={f.label}>
                    <p className="text-xs mb-1" style={{ color: 'var(--chrome-dim)' }}>{f.label}</p>
                    <p className="text-sm font-medium truncate" style={{ color: f.value ? 'var(--paper)' : 'var(--chrome-dim)', fontStyle: f.value ? 'normal' : 'italic' }}>
                      {f.value || 'Not set'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hub Grid */}
        {visibleHubs.length > 0 && (
          <div className={`grid grid-cols-2 lg:grid-cols-3 ${gapClass} hb-hub-grid`}>
            {visibleHubs.map(hub => (
              <Link
                key={hub.href}
                href={hub.href}
                className={`hb-tap-feedback card-elevated rounded-2xl ${padClass} flex flex-col gap-3 group transition-all duration-200 hover:scale-[1.01] ${hub.span}`}
                style={{ textDecoration: 'none', borderColor: hub.border, minHeight: 100 }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: hub.bg }}>
                  {hub.icon}
                </div>
                <div className="flex-1">
                  <p className="hb-body text-base font-semibold mb-0.5" style={{ color: 'var(--paper)' }}>{hub.label}</p>
                  <p className="text-xs hb-landscape-hide" style={{ color: 'var(--chrome-dim)' }}>{hub.sub}</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: hub.color }}>
                  Open
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6h7M6.5 3l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty hub state */}
        {visibleHubs.length === 0 && (
          <div className="rounded-2xl py-12 flex flex-col items-center gap-3 text-center" style={{ background: 'rgba(168,176,186,0.04)', border: '1px dashed rgba(168,176,186,0.15)' }}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" style={{ color: 'var(--chrome-dim)', opacity: 0.5 }}>
              <rect x="4" y="4" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="17" y="4" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="4" y="17" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="17" y="17" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            <p className="text-sm font-medium" style={{ color: 'var(--chrome-dim)' }}>All hub cards are hidden</p>
            <button onClick={() => setPanelOpen(true)} className="text-xs font-semibold" style={{ color: '#3B9EFF' }}>Open Customise panel</button>
          </div>
        )}

        {/* Quick Links Row */}
        {prefs.showQuickLinks && visibleQuickLinks.length > 0 && (
          <div className={`mt-6 grid grid-cols-2 lg:grid-cols-4 ${gapClass}`}>
            {visibleQuickLinks.map(l => (
              <Link key={l.href} href={l.href} className="hb-touch hb-tap-feedback card-elevated rounded-xl px-4 flex items-center gap-3 transition-all" style={{ textDecoration: 'none', minHeight: 56 }}>
                <span className="text-lg">{l.icon}</span>
                <span className="hb-body text-sm font-medium" style={{ color: 'var(--paper)' }}>{l.label}</span>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="ml-auto" style={{ color: 'var(--chrome-dim)' }}><path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
